/**
 * This file is responsible for checking if the user is authenticated and redirecting to the appropriate sign in page.
 * It's bundled separately from the rest of the app to ensure it's loaded as early as possible and runs immediately when loaded.
 * This is why you don't see it directly called anywhere in the app.
 */

import { authnClient } from '@frontend/auth';
import { isWeaveTokenBufferActive, setLastVisitedPage, AUTH_CHECK_IGNORE_ROUTES } from '@frontend/auth-helpers';

function isPreviewHost(hostname: string): boolean {
  return (
    hostname.includes('preview.weavelab.xyz') ||
    hostname.includes('f.weavedev.net') ||
    hostname.includes('edge.getweave.com')
  );
}

// previews are for internal use only
function redirectPreview() {
  // client side nav to /sign-in in where they can use legacy auth
  window.history.pushState({}, '', '/sign-in');
}

async function redirectMain() {
  // top level page navigation to gatekeeper in where they use OIDC
  const urlParams = new URLSearchParams(window.location.search);
  await authnClient.signIn({
    meta: { source: 'auth_check' },
    state: {
      ssoTeam: urlParams.get('ssoTeam') ?? undefined,
      ssoTeamHint: urlParams.get('ssoTeamHint') ?? undefined,
    },
  });
}

export async function authQuickCheck() {
  if (AUTH_CHECK_IGNORE_ROUTES.some((route) => location.pathname.includes(route))) {
    return;
  }

  const isUserAuthenticated = authnClient.isUserAuthenticated() || isWeaveTokenBufferActive();
  if (!isUserAuthenticated) {
    const originalURI = window.location.pathname + (window.location.search ?? '') + (window.location.hash ?? '');
    if (originalURI) {
      setLastVisitedPage(originalURI);
    }

    // NOTE intentionally leaving this console.log here for debugging purposes
    console.log('authQuickCheck: triggering sign-in');

    if (isPreviewHost(location.hostname)) {
      return redirectPreview();
    }

    await redirectMain();
  }
}
